import axios from "axios";
import { format } from "date-fns";
import { useCallback, useContext, useRef } from "react";
import { Form, OverlayTrigger, Table, Tooltip } from "react-bootstrap";
import toast from "react-hot-toast";
import useIsOneIntroducer from "../../../hooks/useIsOneIntroducer";
import { UserContext } from "../../../Context/UserContextAPI";

const CollectionTableDataShow = ({
  usersData: data,
  setUsersData,
  refreshData,
}) => {
  const { accessPerm } = useContext(UserContext);

  // custom hooks
  const isOneIntroducer = useIsOneIntroducer();

  const inputRefs = useRef([]);
  const debounceTimerRef = useRef(null);

  // Debounced version of handleChange
  const debouncedHandleChange = useCallback(
    (index, field, value) => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        setUsersData((prevData) => {
          const updatedData = [...prevData];
          const user = updatedData[index];
          const payment = {
            ...user?.userDetail?.user_payment,
            [field]: value,
          };

          // Perform necessary calculations based on field
          if (field === "paid_amount") {
            const diff =
              parseFloat(payment.net_amount || 0) - parseFloat(value || 0);
            payment.commission = diff > 0 ? 0 : diff * -1;
            payment.due = diff > 0 ? diff : 0;
            payment.due_initial = payment.due;
          } else if (field === "net_amount") {
            const diff =
              parseFloat(value || 0) - parseFloat(payment.paid_amount || 0);
            payment.commission = diff > 0 ? 0 : diff * -1;
            payment.due = diff > 0 ? diff : 0;
            payment.due_initial = payment.due;
          } else if (field === "paid") {
            const diff =
              parseFloat(payment.due_initial || 0) - parseFloat(value || 0);
            payment.due = diff > 0 ? diff : diff * -1;
          }
          payment.hasChanged = true;
          payment.hasLocalDataChanged = true;
          updatedData[index].userDetail.user_payment = payment;
          return updatedData;
        });
      }, 1); // 500ms delay
    },
    [setUsersData],
  );

  // Key navigation logic remains the same
  const handleKeyDown = (event, index, fieldName) => {
    const { key, ctrlKey } = event;
    const fields = [
      "paid_amount",
      "net_amount",
      "commission",
      "due",
      "paid",
      "remarks",
    ];

    const selectInputContent = (element) => {
      if (element) {
        element.select();
      }
    };

    if (key === "ArrowDown" && index < data.length - 1) {
      event.preventDefault();
      const nextRow = inputRefs.current[index + 1];
      if (nextRow) {
        const nextFieldIndex = fields.indexOf(fieldName);
        const nextElement = nextRow[nextFieldIndex];
        nextElement?.focus();
        selectInputContent(nextElement);
      }
    } else if (key === "ArrowUp" && index > 0) {
      event.preventDefault();
      const prevRow = inputRefs.current[index - 1];
      if (prevRow) {
        const prevFieldIndex = fields.indexOf(fieldName);
        const prevElement = prevRow[prevFieldIndex];
        prevElement?.focus();
        selectInputContent(prevElement);
      }
    } else if (key === "ArrowRight") {
      event.preventDefault();
      const nextFieldIndex = fields.indexOf(fieldName) + 1;
      if (nextFieldIndex < fields.length) {
        const nextElement = inputRefs.current[index][nextFieldIndex];
        nextElement?.focus();
        selectInputContent(nextElement);
      }
    } else if (key === "ArrowLeft") {
      event.preventDefault();
      const prevFieldIndex = fields.indexOf(fieldName) - 1;
      if (prevFieldIndex >= 0) {
        const prevElement = inputRefs.current[index][prevFieldIndex];
        prevElement?.focus();
        selectInputContent(prevElement);
      }
    }
  };

  // total calculations

  const totalReceived = data?.reduce(
    (acc, user) =>
      acc + parseFloat(user?.userDetail?.user_payment?.paid_amount || 0),
    0,
  );
  const totalNetAmount = data?.reduce(
    (acc, user) =>
      acc + parseFloat(user?.userDetail?.user_payment?.net_amount || 0),
    0,
  );
  const totalcommission = data?.reduce(
    (acc, user) =>
      acc + parseFloat(user?.userDetail?.user_payment?.commission || 0),
    0,
  );
  const totalDue = data?.reduce(
    (acc, user) => acc + parseFloat(user?.userDetail?.user_payment?.due || 0),
    0,
  );

  const totalPaid = data?.reduce(
    (acc, user) => acc + parseFloat(user?.userDetail?.user_payment?.paid || 0),
    0,
  );

  const handleUserStatusChange = async (id, is_active) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/v2/users/update/user-active-status/${id}`,
        { is_active: is_active === "1" ? true : false },
      );
      console.log(response);
      if (response?.data?.status === "OK") {
        toast.success(response?.data?.data);
        refreshData();
      }
    } catch (error) {
      console.log(error);
    }
  };

  function columnBackgroundColor(user) {
    if (!user?.is_active) {
      return "#f00";
    }

    if (
      user?.userDetail?.user_payment?.hasChanged ||
      user?.userDetail?.user_payment?.hasLocalDataChanged
    ) {
      return "#faf1cd";
    }
    return "";
  }

  return (
    <Table
      // striped
      bordered
      // hover
      className=" w-100 align-middle  mb-0 rounded position-relative overflow-x-scroll"
    >
      <thead
        className="w-100"
        style={{
          position: "sticky",
          top: 0,
          color: "white",
          // zIndex: 1,
          backgroundColor: "#095d7e",
        }}
      >
        <tr>
          <th>SL</th>
          <th style={{ width: "100px" }}>Date</th>
          <th>Reg. NO</th>
          <th>Passenger</th>
          <th>Passport No</th>
          {!isOneIntroducer && <th>Agency</th>}
          <th>Country</th>
          <th>Test/Ref By</th>
          <th style={{ width: "80px" }}>Received</th>
          <th style={{ width: "100px" }}>Net Amount</th>
          <th style={{ width: "80px" }}>Balance</th>
          <th style={{ width: "80px" }}>Dues</th>
          <th style={{ width: "80px" }}>Paid</th>
          {accessPerm(76, 36) && (
            <th style={{ width: "100px" }}>User Status</th>
          )}
          <th style={{ width: "120px" }}>Remarks</th>
        </tr>
      </thead>

      <tbody
      // style={{
      //   backgroundColor: "#095d7e",
      // }}
      >
        {data.map((user, index) => {
          const userTest = user?.userTest || [];
          const userTestInvestigation = userTest
            ?.map((test) => test?.investigation?.code)
            .join(", ");
          return (
            <tr
              style={{
                backgroundColor: columnBackgroundColor(user),
              }}
              key={user?.id}
              className={`${
                user?.userDetail?.user_payment?.commission > 0
                  ? "text-primary"
                  : ""
              }  ${
                user?.userDetail?.user_payment?.due > 0 ? "text-danger" : ""
              } `}
            >
              <td>{index + 1}</td>
              <td>{format(new Date(user?.createdAt), "dd-MM-yyyy")}</td>
              <td>
                {user?.reg_prefix}
                {user?.registration_no}
              </td>
              <td>{user?.f_name}</td>
              <td>{user?.passport_no}</td>
              {!isOneIntroducer && (
                <td>{user?.userDetail?.introducer_user?.f_name}</td>
              )}
              <td>{user?.userDetail?.country?.name}</td>
              <td>
                {user?.role_id === 5 ? (
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip>{userTestInvestigation}</Tooltip>}
                  >
                    <span>
                      {userTestInvestigation?.substring(0, 15) +
                        `${userTestInvestigation?.length > 15 ? "..." : ""}`}
                    </span>
                  </OverlayTrigger>
                ) : (
                  user?.userDetail?.package?.name
                )}
              </td>

              <td>
                <Form.Control
                  className={`${
                    user?.userDetail?.user_payment?.commission > 0
                      ? "text-primary"
                      : ""
                  }  ${
                    user?.userDetail?.user_payment?.due > 0 ? "text-danger" : ""
                  } `}
                  ref={(el) => {
                    if (!inputRefs.current[index])
                      inputRefs.current[index] = [];
                    inputRefs.current[index][0] = el;
                  }}
                  // value={user?.userDetail?.user_payment?.paid_amount || 0}
                  defaultValue={user?.userDetail?.user_payment?.paid_amount}
                  onKeyDown={(e) => handleKeyDown(e, index, "paid_amount")}
                  onChange={(e) =>
                    debouncedHandleChange(index, "paid_amount", e.target.value)
                  }
                />
              </td>
              <td>
                <Form.Control
                  className={`${
                    user?.userDetail?.user_payment?.commission > 0
                      ? "text-primary"
                      : ""
                  }  ${
                    user?.userDetail?.user_payment?.due > 0 ? "text-danger" : ""
                  } `}
                  ref={(el) => {
                    if (!inputRefs.current[index])
                      inputRefs.current[index] = [];
                    inputRefs.current[index][1] = el;
                  }}
                  // value={user?.userDetail?.user_payment?.net_amount || 0}
                  defaultValue={user?.userDetail?.user_payment?.net_amount}
                  onKeyDown={(e) => handleKeyDown(e, index, "net_amount")}
                  onChange={(e) =>
                    debouncedHandleChange(index, "net_amount", e.target.value)
                  }
                />
              </td>
              <td>
                <Form.Control
                  className={`${
                    user?.userDetail?.user_payment?.commission > 0
                      ? "text-primary"
                      : ""
                  }  ${
                    user?.userDetail?.user_payment?.due > 0 ? "text-danger" : ""
                  } `}
                  ref={(el) => {
                    if (!inputRefs.current[index])
                      inputRefs.current[index] = [];
                    inputRefs.current[index][2] = el;
                  }}
                  value={user?.userDetail?.user_payment?.commission}
                  defaultValue={user?.userDetail?.user_payment?.commission}
                  onKeyDown={(e) => handleKeyDown(e, index, "commission")}
                  onChange={(e) =>
                    debouncedHandleChange(index, "commission", e.target.value)
                  }
                />
              </td>
              <td>
                <Form.Control
                  className={`${
                    user?.userDetail?.user_payment?.commission > 0
                      ? "text-primary"
                      : ""
                  }  ${
                    user?.userDetail?.user_payment?.due > 0 ? "text-danger" : ""
                  } `}
                  ref={(el) => {
                    if (!inputRefs.current[index])
                      inputRefs.current[index] = [];
                    inputRefs.current[index][3] = el;
                  }}
                  value={user?.userDetail?.user_payment?.due}
                  defaultValue={user?.userDetail?.user_payment?.due}
                  onKeyDown={(e) => handleKeyDown(e, index, "due")}
                  onChange={(e) =>
                    debouncedHandleChange(index, "due", e.target.value)
                  }
                />
              </td>
              <td>
                <Form.Control
                  className={`${
                    user?.userDetail?.user_payment?.commission > 0
                      ? "text-primary"
                      : ""
                  }  ${
                    user?.userDetail?.user_payment?.due > 0 ? "text-danger" : ""
                  } `}
                  ref={(el) => {
                    if (!inputRefs.current[index])
                      inputRefs.current[index] = [];
                    inputRefs.current[index][4] = el;
                  }}
                  // value={user?.userDetail?.user_payment?.paid}
                  defaultValue={user?.userDetail?.user_payment?.paid}
                  onKeyDown={(e) => handleKeyDown(e, index, "paid")}
                  onChange={(e) =>
                    debouncedHandleChange(index, "paid", e.target.value)
                  }
                />
              </td>

              {accessPerm(76, 36) && (
                <td>
                  <select
                    className="form-select"
                    onChange={(e) =>
                      handleUserStatusChange(user?.id, e.target.value)
                    }
                    defaultValue={user?.is_active ? "1" : "0"}
                  >
                    <option value="1">Active</option>
                    <option value="0">Cancelled</option>
                  </select>

                  {/* {user?.is_active ? (
                  <button
                    className="btn btn-success"
                    onClick={() => handleUserStatusChange(user?.id, "0")}
                  >
                    Active
                  </button>
                ) : (
                  <button
                    className="btn btn-danger"
                    onClick={() => handleUserStatusChange(user?.id, "1")}
                  >
                    Cancelled
                  </button>
                )} */}
                </td>
              )}

              <td>
                <Form.Control
                  className={`${
                    user?.userDetail?.user_payment?.commission > 0
                      ? "text-primary"
                      : ""
                  }  ${
                    user?.userDetail?.user_payment?.due > 0 ? "text-danger" : ""
                  } `}
                  ref={(el) => {
                    if (!inputRefs.current[index])
                      inputRefs.current[index] = [];
                    inputRefs.current[index][5] = el;
                  }}
                  // value={user?.user_payment?.remarks || ""}
                  defaultValue={user?.userDetail?.user_payment?.remarks || ""}
                  onKeyDown={(e) => handleKeyDown(e, index, "remarks")}
                  onChange={(e) =>
                    debouncedHandleChange(index, "remarks", e.target.value)
                  }
                />
              </td>
            </tr>
          );
        })}
        <tr
          style={{
            position: "sticky",
            bottom: "-1px",
            backgroundColor: "#095d7e",
            color: "white",
          }}
        >
          <td />

          <td />
          <td />

          <td />
          <td />
          <td />
          <td>Total</td>
          <td>{totalReceived}</td>
          <td>{totalNetAmount}</td>

          <td>{totalcommission}</td>
          <td>{totalDue}</td>
          <td>{totalPaid}</td>
          {accessPerm(76, 36) && <td />}
          <td />
        </tr>
      </tbody>
    </Table>
  );
};

export default CollectionTableDataShow;
